import * as React from "react";
import { useI18n } from "../../lib/i18n";
import type { CommandExecution } from "../../lib/api/commands";
import * as s from "./commands.css";

function bodyOf(payload: unknown): unknown {
  if (payload && typeof payload === "object" && !Array.isArray(payload) && "body" in payload) {
    return (payload as { body: unknown }).body;
  }
  return payload;
}

function firstRecord(value: unknown): Record<string, unknown> | null {
  const candidate = Array.isArray(value) ? value[0] : value;
  return candidate && typeof candidate === "object" && !Array.isArray(candidate)
    ? (candidate as Record<string, unknown>)
    : null;
}

function display(value: unknown): string {
  if (value === undefined || value === null || value === "") return "—";
  return typeof value === "object" ? JSON.stringify(value) : String(value);
}

function RegisterTable({ execution }: { execution: CommandExecution }) {
  const { t } = useI18n();
  const result = firstRecord(bodyOf(execution.responsePayload)) ?? {};
  const written = (execution.params?.values ?? null) as Record<string, number> | null;

  if (!written) {
    const rows = Object.entries(result);
    if (rows.length === 0) return <span className={s.muted}>{t("pro.commands.response.empty")}</span>;
    return (
      <table className={s.table}>
        <thead>
          <tr>
            <th className={s.th}>{t("pro.commands.params.register")}</th>
            <th className={s.th}>{t("pro.commands.params.value")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([register, value]) => (
            <tr key={register}>
              <td className={s.td}>{register}</td>
              <td className={s.td}>{display(value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  const before = firstRecord(execution.preReadResult) ?? {};
  return (
    <table className={s.table}>
      <thead>
        <tr>
          <th className={s.th}>{t("pro.commands.params.register")}</th>
          <th className={s.th}>{t("pro.commands.response.before")}</th>
          <th className={s.th}>{t("pro.commands.response.requested")}</th>
          <th className={s.th}>{t("pro.commands.response.result")}</th>
        </tr>
      </thead>
      <tbody>
        {Object.entries(written).map(([register, value]) => {
          const previous = before[register];
          const changed = previous !== undefined && Number(previous) !== Number(value);
          return (
            <tr key={register}>
              <td className={s.td}>{register}</td>
              <td className={s.td}>{display(previous)}</td>
              <td className={s.td}>
                <span className={changed ? s.changed : undefined}>{display(value)}</span>
              </td>
              <td className={s.td}>{display(result[register])}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function KeyValues({ value }: { value: unknown }) {
  const { t } = useI18n();
  const record = firstRecord(value);
  if (!record || Object.keys(record).length === 0) {
    return <span className={s.muted}>{t("pro.commands.response.empty")}</span>;
  }
  return (
    <div className={s.kvGrid}>
      {Object.entries(record).map(([key, v]) => (
        <React.Fragment key={key}>
          <span className={s.kvKey}>{key}</span>
          <span>{display(v)}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

export function CommandResponseView({ execution }: { execution: CommandExecution }) {
  const { t } = useI18n();
  if (execution.responsePayload == null) {
    return <span className={s.muted}>{t("pro.commands.response.none")}</span>;
  }
  switch (execution.responseRender) {
    case "registers":
      return <RegisterTable execution={execution} />;
    case "kv":
      return <KeyValues value={bodyOf(execution.responsePayload)} />;
    case "status":
      return (
        <span className={s.muted}>
          {execution.status === "succeeded" ? t("pro.commands.response.confirmed") : execution.error ?? "—"}
        </span>
      );
    default:
      return <pre className={s.code}>{JSON.stringify(bodyOf(execution.responsePayload), null, 2)}</pre>;
  }
}

export function JsonBlock({ value }: { value: unknown }) {
  return <pre className={s.code}>{value == null ? "—" : JSON.stringify(value, null, 2)}</pre>;
}
